/**
 * Fax procedure questionnaire layout: loads questions from Firestore, edits a
 * branching tree, submits to the backend, then polls or navigates to final documents.
 *
 * State, effects, and the submit flow live in {@link useProcedureQuestionnaire};
 * this component composes the header, questionnaire body, and submit footer.
 *
 * @module procedureQuestionaire/procedureQuestionaireLayout
 */
import { Footer } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { MedicalPaOrderStatus } from "../../enums/medicalPaOrderStatus";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import FormQuestions from "./components/formQuestions/formQuestions";
import useProcedureQuestionnaire from "./hooks/useProcedureQuestionnaire";

/**
 * Fax workflow questionnaire page for an order (`id` route param).
 *
 * @returns Loader or the questionnaire body with submit footer
 */
const ProcedureQuestionaireLayout = () => {
  const {
    id,
    order,
    generalOrderDetailsLoading,
    faxDataToSend,
    isFaxDataLoading,
    questionnaireStatus,
    isSubmitting,
    handleAnswersChange,
    handleSubmit,
    navigateBack,
  } = useProcedureQuestionnaire();

  return (
    <>
      {generalOrderDetailsLoading ? (
        <>
          <LoaderMessage message="Fetching form questions..." />
        </>
      ) : (
        <div className="procedure-pa-form__container flex h-full flex-col bg-[white]">
          <div className="procedure-pa-form__body flex flex-1 flex-col overflow-hidden rounded px-4 py-2">
            <div className="procedure-pa-form__body--header mb-4">
              <div className="procedure-pa-form__body--header-details flex items-center gap-2">
                <div className="procedure-pa-form__body patient-name text-h16 font-semibold leading-6 text-primaryGray-1">
                  {generateFullName(
                    order?.demographics?.first_name ?? "",
                    "",
                    order?.demographics?.last_name ?? "",
                  )}
                </div>
                <div className="procedure-pa-form__body page-name text-sm font-semiBold text-black">
                  {" "}
                  (
                  {order?.demographics?.patient_id ??
                    order?.demographics?.patient_mrn ??
                    ""}
                  )
                </div>
              </div>
            </div>
            <div className="procedure-pa-form__body content flex flex-1 overflow-hidden">
              <FormQuestions
                enableSubmitButton={() => {}}
                makePostCallForFormSubmission={false}
                docId={id ?? ""}
                isLoading={(value) => {}}
                faxData={faxDataToSend}
                questionnaireStatus={questionnaireStatus}
                isQuestionnaireLoading={isFaxDataLoading}
                onAnswersChange={handleAnswersChange}
              />
            </div>
          </div>

          <div className="procedure-pa-form__footer">
            <Footer
              cancelButtonText="Go Back"
              submitButtonText={
                isSubmitting
                  ? "Reviewing..."
                  : order?.status?.financial_review ===
                        MedicalPaOrderStatus.PENDING ||
                      order?.status?.financial_review ===
                        MedicalPaOrderStatus.DENIED ||
                      order?.status?.financial_review ===
                        MedicalPaOrderStatus.APPROVED
                    ? "Go Back to Orders"
                    : "Review"
              }
              onCancelClick={() => navigateBack(null)}
              onSubmitClick={handleSubmit}
              isDisabled={
                questionnaireStatus === "in_progress" || isSubmitting
                  ? true
                  : false
              }
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ProcedureQuestionaireLayout;
